import React from 'react'
import { StarsAndMoonsComponent } from '~/react/common-components/StarsAndMoonsComponent'
import { useTranslation } from 'react-i18next'
import { Employee } from '~/app/types'
import { Link } from 'react-router-dom'
import { isCheckInEnabled, isTimeOffEnabled } from '~/utils/OWUtils'
import { Dropdown } from 'react-bootstrap'
import { TEAM_PAGE_TABS } from '~/react/tabs'
import {
    showTeamCheckInLink,
    showTeamManagerFeedbackLink,
    showTeamTimeOffLink,
} from '~/react/employee-team/EmployeeTeamLinks'
import { urlForTeam } from '~/utils/PlaceManager'

/**
 * Contains the stars/moons as well as the reports/descendants counts.
 */
type Props = {
    employee: Employee
    showTimeOff?: boolean
    showCheckIn?: boolean
    showManagerFeedback?: boolean
    showRemi?: boolean
    remiUrl?: string | null
    showBackButton?: boolean
}

const ToolsMenuToggle = React.forwardRef<HTMLButtonElement, { onClick: (e: React.MouseEvent) => void }>(
    ({ onClick }, ref) => {
        return (
            <button
                onClick={onClick}
                id={'employee-header-btn-tools'}
                className={
                    'btn btn-sm btn-default dropdown-toggle tooltip-toggle hide-on-native-mobile btn-guest-allowed'
                }
                title={'Tools'}
                ref={ref}
            >
                Tools
            </button>
        )
    }
)

export const EmployeeBadgeMetricsWithoutDurationOrangesDependency = ({
    employee,
    showTimeOff,
    showCheckIn,
    showManagerFeedback,
    showRemi,
    remiUrl,
    showBackButton,
}: Props) => {
    const {
        employment_duration,
        prior_employment_duration,
        prior_company_name,
        is_contractor,
        direct_reports_count: directReportsCount,
        descendants_count: descendantsCount,
        public_id,
    } = employee
    const [t] = useTranslation()
    const hasReports = Boolean(directReportsCount)
    const hasDescendants = hasReports && descendantsCount !== directReportsCount

    const showTimeOffLink = showTimeOff && isTimeOffEnabled()
    const showCheckInLink = showCheckIn && isCheckInEnabled()
    const showManagerFeedbackLink = showManagerFeedback

    const showToolsMenu = showTimeOffLink || showCheckInLink || showManagerFeedbackLink || showRemi

    const teamToolsLink = React.forwardRef<
        HTMLAnchorElement,
        React.HTMLProps<HTMLButtonElement> & { employeeSlug: string; tab: string }
    >(({ employeeSlug, tab, children }, ref) => {
        return (
            <Dropdown.Item
                className="minimal-effects tools-dropdown-item"
                onClick={() => {
                    globalThis.location.href = urlForTeam({
                        employeeSlug: employeeSlug,
                        v: tab,
                    })
                }}
                ref={ref}
            >
                {children}
            </Dropdown.Item>
        )
    })

    return (
        <div className="metrics-container ">
            <StarsAndMoonsComponent
                employmentDuration={employment_duration}
                priorEmploymentDuration={prior_employment_duration}
                priorCompanyName={prior_company_name}
                isContractor={is_contractor}
            />
            {hasDescendants && (
                <div
                    className="node-counts"
                    title={t('profile_page.direct_and_total_reports_count', {
                        directReportsCount,
                        descendantsCount,
                    })}
                >
                    {t('count', { count: directReportsCount })} &middot; {t('count', { count: descendantsCount })}
                </div>
            )}

            {hasReports && !hasDescendants && (
                <div
                    className="node-counts"
                    title={t('profile_page.direct_reports_count', {
                        directReportsCount,
                    })}
                >
                    {t('count', { count: directReportsCount })}
                </div>
            )}
            {showBackButton ? (
                <Link to={`/employees/${public_id}`} id={'employee-header-btn-back'} className="btn btn-primary">
                    Back to Profile
                </Link>
            ) : (
                showToolsMenu && (
                    <Dropdown align={'end'}>
                        <Dropdown.Toggle as={ToolsMenuToggle} />
                        <Dropdown.Menu>
                            {showTimeOffLink && (
                                <Dropdown.Item
                                    className={'minimal-effects time-off-link'}
                                    to={`/employees/${public_id}/time_off/`}
                                    as={Link}
                                >
                                    Time Off
                                </Dropdown.Item>
                            )}
                            {showCheckInLink && (
                                <Dropdown.Item
                                    className={'minimal-effects check-in-link'}
                                    to={`/employees/${public_id}/check_in/`}
                                    as={Link}
                                >
                                    Check In
                                </Dropdown.Item>
                            )}
                            {showManagerFeedbackLink && (
                                <Dropdown.Item
                                    className={'minimal-effects manager-feedback-link'}
                                    to={`/employees/${public_id}/manager_feedback/`}
                                    as={Link}
                                >
                                    Manager Feedback
                                </Dropdown.Item>
                            )}
                            {showRemi &&
                                (remiUrl ? (
                                    <Dropdown.Item
                                        className="minimal-effects remi-link"
                                        href={remiUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Remi
                                    </Dropdown.Item>
                                ) : (
                                    <Dropdown.Item className="minimal-effects remi-link" disabled>
                                        Remi (no scores found)
                                    </Dropdown.Item>
                                ))}
                            {hasReports && (
                                <>
                                    <li className="divider" role="separator" />
                                    <Dropdown.Item
                                        role="menuitem"
                                        as={teamToolsLink}
                                        employeeSlug={public_id}
                                        tab={TEAM_PAGE_TABS.members}
                                    >
                                        Team Members
                                    </Dropdown.Item>
                                    {showTeamTimeOffLink && (
                                        <Dropdown.Item
                                            role="menuitem"
                                            as={teamToolsLink}
                                            employeeSlug={public_id}
                                            tab={TEAM_PAGE_TABS.timeOff}
                                        >
                                            Team Time Off
                                        </Dropdown.Item>
                                    )}
                                    {showTeamCheckInLink(employee) && (
                                        <Dropdown.Item
                                            role="menuitem"
                                            as={teamToolsLink}
                                            employeeSlug={public_id}
                                            tab={TEAM_PAGE_TABS.checkIn}
                                        >
                                            Team Check Ins
                                        </Dropdown.Item>
                                    )}
                                    {showTeamManagerFeedbackLink(employee) && (
                                        <Dropdown.Item
                                            role="menuitem"
                                            as={teamToolsLink}
                                            employeeSlug={public_id}
                                            tab={TEAM_PAGE_TABS.managerFeedback}
                                        >
                                            Team Manager Feedback
                                        </Dropdown.Item>
                                    )}
                                </>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            )}
        </div>
    )
}
